import { useContext, useState, useRef } from "react";
import { Box, Button, Flex, Text, VStack } from "@chakra-ui/react";
import { ProgressSteps } from "./components/Steps";
import "./index.css";
import { UserProfileContext } from "../../context/UserContext";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { authClearUser } from "../../store/actions/authAction";
import { verifyPayment } from "./services/verifyPayment";
import { pollBillStatus } from "./services/pollBill";
import {
  getStoredPendingPayment,
  clearStoredPendingPayment,
} from "./services/pendingPaymentContext";
import { successNotifier } from "../../components/NotificationHandler";
import FullPageLoader from "../../components/fullPageLoader";
import { PaymentRecordTable } from "../../components/PaymentRecordTable";

const TERMINAL_STATUSES = new Set(["CONFIRMED", "FAILED", "REFUNDED", "CANCELLED", "ABANDONED", "DECLINED"]);

export const TopUp = () => {
  const [loading, setLoading] = useState(false);
  const [resumedPayment, setResumedPayment] = useState(null);
  // Set only when there is a pending payment to resume but the resident is
  // not currently authenticated — never attempt an unauthenticated status or
  // token lookup, always require sign-in first (Checkpoint 2B2 Final Closure).
  const [pendingSignIn, setPendingSignIn] = useState(null);
  const { userProfile } = useContext(UserProfileContext) || {};
  const activeAccount = useSelector((state) => state?.account?.activeAccount);
  // tableKey is incremented after payment verify to force PaymentRecordTable to reset to page 1
  const [tableKey, setTableKey] = useState(0);

  const estateId = activeAccount?.estateId || null;

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pollCancelRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollCancelRef.current) pollCancelRef.current();
    };
  }, []);

  // Resume a payment either from Paystack's redirect query params, or (on a
  // plain page refresh before Paystack has redirected back) from the
  // pending-payment context stored at initiation time.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlReference = params.get("reference") || params.get("trxref");
    const urlBillId = params.get("billId");

    const stored = getStoredPendingPayment();
    const billId = urlBillId || stored?.billId;
    const reference = urlReference || stored?.reference;

    if (!billId) return;

    const token = localStorage.getItem("WMS#221#");
    if (!token) {
      // The resident's session is gone — e.g. they signed out (or their
      // session expired) in another tab while away completing the Paystack
      // checkout. Do not call the public-verify endpoint or the authenticated
      // status endpoint: both would either fail or (for public-verify) must
      // never be trusted to reveal a token to an unauthenticated caller.
      // The pending payment context (billId/reference/createdAt only) is
      // already persisted from initiation time and survives sign-in, so this
      // resumes automatically once the resident is back in.
      setPendingSignIn({ billId, reference });
      return;
    }

    setLoading(true);

    const startPolling = () => {
      const { promise, cancel } = pollBillStatus(billId, {
        onStatus: (data) => {
          if (!mountedRef.current) return;
          setResumedPayment({
            billId,
            reference,
            status: data.status,
            token: data.token,
          });
        },
      });
      pollCancelRef.current = cancel;
      promise
        .then((token) => {
          if (!mountedRef.current) return;
          setResumedPayment({ billId, reference, status: "CONFIRMED", token });
          clearStoredPendingPayment();
        })
        .catch((err) => {
          if (!mountedRef.current) return;
          const status = err?.reconciliationRequired
            ? "RECONCILIATION_REQUIRED"
            : /refund/i.test(err?.message || "")
              ? "REFUNDED"
              : /failed/i.test(err?.message || "")
                ? "FAILED"
                : "OUTCOME_UNKNOWN";
          setResumedPayment({ billId, reference, status, token: null });
          if (TERMINAL_STATUSES.has(status)) clearStoredPendingPayment();
        });
    };

    // One immediate verify attempt for the fastest possible resolution —
    // then fall back to polling regardless of its outcome, since the
    // webhook (or another concurrent verify) may complete it independently.
    // Always the authenticated endpoint — the resident frontend never relies
    // on the public-verify endpoint for status or token delivery.
    verifyPayment({ reference, billId })
      .then((resp) => {
        if (!mountedRef.current) return;
        const status = resp?.data?.status;
        if (status && TERMINAL_STATUSES.has(status)) {
          // Success is only ever announced for a server-CONFIRMED payment —
          // never inferred from the callback URL.
          if (status === "CONFIRMED") {
            successNotifier(resp?.data?.response || "Payment status updated");
          }
          setResumedPayment({ billId, reference, status, token: resp?.data?.token || null });
          setTableKey((k) => k + 1);
          clearStoredPendingPayment();
        } else {
          startPolling();
        }
      })
      .catch(() => {
        // Verify failing (network blip, race with webhook) is not fatal —
        // poll for the authoritative state instead of surfacing an error.
        startPolling();
      })
      .finally(() => {
        if (!mountedRef.current) return;
        setLoading(false);
        if (urlBillId || urlReference) {
          navigate("/buy-water", { replace: true });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Force a clean sign-out so App re-renders the unauthenticated app, which
  // redirects to /login?next=<this exact URL> — the resident lands back here
  // (billId/reference preserved in the URL) once they've signed back in.
  const handleSignIn = () => {
    localStorage.removeItem("WMS#221#");
    localStorage.removeItem("WMS_USER");
    dispatch(authClearUser());
  };

  if (pendingSignIn) {
    return (
      <Flex align="center" justify="center" w="100%" mt="80px">
        <VStack spacing={3} maxW="360px" textAlign="center">
          <Text fontSize="20px" fontWeight="bold" color="#050509">
            Sign in to view the status of your payment
          </Text>
          <Text fontSize="14px" color="gray.600">
            Your payment is saved. Sign in to resume and view your token.
          </Text>
          <Button colorScheme="primary" bg="primary.500" color="white" onClick={handleSignIn}>
            Sign In
          </Button>
        </VStack>
      </Flex>
    );
  }

  return loading ? (
    <FullPageLoader />
  ) : (
    <Box>
      <Box
        color="primary.500"
        fontSize={"27px"}
        fontWeight="bold"
        py={"20px"}
        pl={["5px", "5px", "0"]}
      ></Box>
      <Box w={["100%", "100%", "100%"]} mb={["20px", "20px", "0px"]}>
        <ProgressSteps
          userProfile={userProfile}
          resumedPayment={resumedPayment}
          onDismissResume={() => {
            if (pollCancelRef.current) pollCancelRef.current();
            setResumedPayment(null);
          }}
        />
      </Box>
      {/* Payment records removed from this page; available under Payments menu */}
    </Box>
  );
};
